/** 63-day swing Fib retracement proximity + scoring (api/scan.js + App.jsx). */

import { emaLatest } from "./movingAverages.js";

const PROX_PCT = 0.015;
const SWING_DAYS = 63;

function sma(arr, n) {
  if (!arr?.length || arr.length < n) return null;
  return arr.slice(0, n).reduce((s, v) => s + v, 0) / n;
}

function calcCMF(h, l, c, v, n = 20) {
  if (!c?.length || c.length < n) return null;
  let mfv = 0;
  let vol = 0;
  for (let i = 0; i < n; i++) {
    const hl = h[i] - l[i];
    if (!hl) continue;
    mfv += ((c[i] - l[i]) - (h[i] - c[i])) / hl * v[i];
    vol += v[i];
  }
  return vol ? mfv / vol : 0;
}

function calcTrendStructure(highs, lows, closes, n = 10) {
  if (!closes?.length || closes.length < n * 2) return null;
  const recent = { h: Math.max(...highs.slice(0, n)), l: Math.min(...lows.slice(0, n)) };
  const prior = { h: Math.max(...highs.slice(n, n * 2)), l: Math.min(...lows.slice(n, n * 2)) };
  const hh = recent.h > prior.h;
  const hl = recent.l > prior.l;
  const lh = recent.h < prior.h;
  const ll = recent.l < prior.l;
  return { bullish: hh && hl, bearish: lh && ll };
}

function nearLevel(price, level) {
  if (price == null || level == null || level <= 0) return false;
  return Math.abs(price - level) / level <= PROX_PCT;
}

function closestFibLevel(price, levels) {
  let best = null;
  let bestDist = Infinity;
  for (const row of levels) {
    if (!nearLevel(price, row.value)) continue;
    const d = Math.abs(price - row.value) / row.value;
    if (d < bestDist) {
      bestDist = d;
      best = row.label;
    }
  }
  return best;
}

export function computeSwingFibLevels(highs, lows, n = SWING_DAYS) {
  if (!highs?.length || !lows?.length) return null;
  const len = Math.min(n, highs.length, lows.length);
  const swingHigh = Math.max(...highs.slice(0, len));
  const swingLow = Math.min(...lows.slice(0, len));
  const range = swingHigh - swingLow;
  if (!range || range <= 0) return null;
  return {
    swingHigh,
    swingLow,
    range,
    fib382: swingHigh - range * 0.382,
    fib500: swingHigh - range * 0.5,
    fib618: swingHigh - range * 0.618,
    fib786: swingHigh - range * 0.786,
  };
}

/**
 * @returns {{ fibLevel: string|null, fibBounce: boolean, fibScore: number, fibSwing: object|null }}
 */
export function computeFibRetracementScore({
  highs,
  lows,
  closes,
  volumes,
  price,
  cmf: cmfIn,
  uptrendIntact: uptrendIn,
  todayLow,
  todayClose,
}) {
  const empty = { fibLevel: null, fibBounce: false, fibScore: 0, fibSwing: null };
  if (!highs?.length || !lows?.length || !closes?.length || price == null) return empty;

  const fibSwing = computeSwingFibLevels(highs, lows);
  if (!fibSwing) return empty;

  const levels = [
    { label: "38.2%", value: fibSwing.fib382 },
    { label: "50%", value: fibSwing.fib500 },
    { label: "61.8%", value: fibSwing.fib618 },
    { label: "78.6%", value: fibSwing.fib786 },
  ];

  const cmf =
    cmfIn != null
      ? cmfIn
      : volumes?.length
      ? calcCMF(highs, lows, closes, volumes)
      : null;

  let uptrendIntact = uptrendIn;
  if (uptrendIntact == null) {
    const e50 = emaLatest(closes, 50);
    const e200 = emaLatest(closes, 200);
    const goldenCross = e50 != null && e200 != null && e50 > e200;
    const ts = calcTrendStructure(highs, lows, closes);
    uptrendIntact = goldenCross || ts?.bullish === true;
  }

  const fibLevel = closestFibLevel(price, levels);
  let fibScore = 0;

  if (fibLevel === "38.2%" && uptrendIntact) fibScore = 2;
  else if (fibLevel === "50%" && uptrendIntact) fibScore = 3;
  else if (fibLevel === "61.8%") {
    if (uptrendIntact && (cmf == null || cmf >= 0)) fibScore = 2;
  } else if (fibLevel === "78.6%") fibScore = -1;

  const low = todayLow ?? lows[0];
  const close = todayClose ?? closes[0];
  let fibBounce = false;
  for (const row of levels) {
    if (row.value == null || row.value <= 0) continue;
    const touched = low != null && Math.abs(low - row.value) / row.value <= PROX_PCT;
    const bounced = close != null && close > row.value * 1.01;
    if (touched && bounced) {
      fibBounce = true;
      fibScore += 1;
      break;
    }
  }

  return { fibLevel, fibBounce, fibScore, fibSwing };
}

/** Mutates scan payload with fibLevel, fibBounce, fibScore, fibSwing. */
export function attachFibRetracement(payload) {
  if (!payload?.closes?.length) return payload;
  const price = payload.livePrice ?? payload.closes[0];
  const fib = computeFibRetracementScore({
    highs: payload.highs,
    lows: payload.lows,
    closes: payload.closes,
    volumes: payload.volumes,
    price,
    todayLow: payload.lows?.[0],
    todayClose: payload.closes?.[0],
  });
  payload.fibLevel = fib.fibLevel;
  payload.fibBounce = fib.fibBounce;
  payload.fibScore = fib.fibScore;
  payload.fibSwing = fib.fibSwing;
  return payload;
}
